import {computed} from 'vue';
import {DateAdapterFactory, DateInput} from '@/libs/dates/DateAdapterFactory';
import {useUserStore} from "@/vue/store/userStore";

export enum DateFormat {
    ISO = 'ISO',                                          // 2025-05-09
    Date = 'Date',                                        // 05/09/2025
    ShortDate = 'ShortDate',                              // 05/09
    MonthYear = 'MonthYear',                              // May 2025
    Time = 'Time',                                        // 14:30
    TimeWithSeconds = 'TimeWithSeconds',                  // 14:30:15
    DateTime = 'DateTime',                                // 05/09/2025 14:30
    DateTimeWithSeconds = 'DateTimeWithSeconds',          // 05/09/2025 14:30:15
    LongDate = 'LongDate',                                // May 9, 2025
    LongDateTime = 'LongDateTime',                        // May 9, 2025 14:30
    LongDateWithOrdinal = 'LongDateWithOrdinal',          // May 9th 2025
    FullDateTimeWithSeconds = 'FullDateTimeWithSeconds',  // Friday, May 9, 2025 14:30:15
}

const localeDateFormats: Record<string, Record<DateFormat, string>> = {
    en: {
        [DateFormat.ISO]: 'YYYY-MM-DD',
        [DateFormat.Date]: 'MM/DD/YYYY',
        [DateFormat.ShortDate]: 'MM/DD',
        [DateFormat.MonthYear]: 'MMMM YYYY',
        [DateFormat.Time]: 'HH:mm',
        [DateFormat.TimeWithSeconds]: 'HH:mm:ss',
        [DateFormat.DateTime]: 'MM/DD/YYYY HH:mm',
        [DateFormat.DateTimeWithSeconds]: 'MM/DD/YYYY HH:mm:ss',
        [DateFormat.LongDate]: 'MMM D, YYYY',
        [DateFormat.LongDateWithOrdinal]: 'MMM Do YYYY',
        [DateFormat.LongDateTime]: 'MMM D, YYYY HH:mm',
        [DateFormat.FullDateTimeWithSeconds]: 'dddd, MMM D, YYYY HH:mm:ss',
    },
    es: {
        [DateFormat.ISO]: 'YYYY-MM-DD',
        // [DateFormat.Date]: 'DD/MM/YYYY',
        // [DateFormat.ShortDate]: 'DD/MM',
        [DateFormat.MonthYear]: 'MMMM YYYY',
        [DateFormat.Time]: 'HH:mm',
        [DateFormat.TimeWithSeconds]: 'HH:mm:ss',
        // [DateFormat.DateTime]: 'DD/MM/YYYY HH:mm',
        // [DateFormat.DateTimeWithSeconds]: 'DD/MM/YYYY HH:mm:ss',
        [DateFormat.LongDate]: "D [de] MMM [de] YYYY",
        [DateFormat.LongDateTime]: "D [de] MMM [de] YYYY HH:mm",
        [DateFormat.LongDateWithOrdinal]: 'MMM Do YYYY',
        [DateFormat.FullDateTimeWithSeconds]: "dddd, D [de] MMM [de] YYYY HH:mm:ss",
    },
};

function isDateFormatKey(value: string | DateFormat): value is DateFormat {
    return Object.values(DateFormat).includes(value);
}

export default function useDates() {
    let userStore;
    try {
        userStore = useUserStore();
    } catch {
        userStore = null;
    }

    const user = computed(() => userStore?.user || null)
    const locale = computed(() => user.value?.locale || 'en');

    const currentFormats = computed(() => {
        return localeDateFormats[locale.value] || localeDateFormats['en'];
    });

    const date = (timestamp?: DateInput) => {
        return DateAdapterFactory.create(timestamp, locale.value);
    };

    const format = (
        timestamp?: DateInput,
        formatOrKey: string | DateFormat = DateFormat.Date
    ): string => {
        if (!timestamp) return '';

        const dateInstance = date(timestamp);

        let formatStr: string;

        if (isDateFormatKey(formatOrKey)) {
            formatStr = currentFormats.value?.[formatOrKey] ?? localeDateFormats['en'][formatOrKey];
        } else {
            formatStr = formatOrKey;
        }

        return dateInstance.isValid() ? dateInstance.format(formatStr) : '';
    };

    return {
        date,
        format,
    };
}
